import { MKError } from "../../interfaces";
import { MKProgramRestrictionType } from "../../enums";
import { MKConstants } from "../../constants";

/**
 * Program restriction code & message.
 * @internal
 */
const ProgramRestriction = {
    Rewind: {
        code: "200",
        message: "Rewind is not allowed."
    },
    FastForward: {
        code: "201",
        message: "Fast-forward is not allowed."
    },
    Pause: {
        code: "202",
        message: "Pause is not allowed."
    },
    Resume: {
        code: "203",
        message: "Resume is not allowed."
    },
    SeekForward: {
        code: "204",
        message: "Seek-forward is not allowed."
    },
    SeekBackward: {
        code: "205",
        message: "Seek-backward is not allowed."
    },
    Restart: {
        code: "206",
        message: "Restart is not allowed."
    },
    Timeshift: {
        code: "207",
        message: "Timeshift is not allowed."
    },
    SeekSkip:{
        code: "208",
        message: "Seek-skip is not allowed."
    },
    CatchUp:{
        code: "209",
        message: "Catchup is allowed."
    },
    AndroidBlocked: {
        code: "108",
        message: "Playback is not allowed on android devices"
    },
    IosBlocked: {
        code: "107",
        message: "Playback is not allowed on iOS devices."
    },
    PhoneBlocked: {
        code: "102",
        message: "Playback is not allowed on phone."
    },
    TabletBlocked: {
        code: "103",
        message: "Playback is not allowed on tablet."
    },
    DesktopBlocked: {
        code: "109",
        message: "Playback is not allowed on desktop."
    },
    OutofhomeBlocked: {
        code: "106",
        message: "Playback is not allowed when out of home network coverage."
    },
    AirplayBlocked: {
        code: "301",
        message: "Playback is not allowed for AirPlay."
    },

    //commenting the below two cases as it is currently not supported on this platform
    // CellularBlocked: {
    //     code: "101",
    //     message: "Playback is not allowed over Cellular networks."
    // },
    // WifiBlocked: {
    //     code: "100",
    //     message: "Playback is not allowed over WiFi networks."
    // },
};
Object.freeze(ProgramRestriction);

/**
 * Program restriction error object.
 * @internal
 */
export class MKProgramRestrictionError implements MKError {
    code: string;
    name: string;
    message: string;

    constructor(programRestriction: MKProgramRestrictionType) {
        this.name = "MKProgramRestrictionError";
        const restrictionObj = this.getRestrictionCodeAndMessage(programRestriction);
        this.code = restrictionObj.code;
        this.message = restrictionObj.message;
    }

    private getRestrictionCodeAndMessage(programRestriction: MKProgramRestrictionType): {code: string, message: string} {
        // template "4-80-{code}"
        const errorCodeTemplate = MKConstants.ErrorCodeTemplate.ProgramRestrictionError;
        switch(programRestriction) {
            case MKProgramRestrictionType.Rewind:
                return {
                    code: errorCodeTemplate.replace("{code}", ProgramRestriction.Rewind.code),
                    message: ProgramRestriction.Rewind.message
                };

            case MKProgramRestrictionType.FastForward:
                return {
                    code: errorCodeTemplate.replace("{code}", ProgramRestriction.FastForward.code),
                    message: ProgramRestriction.FastForward.message
                };

            case MKProgramRestrictionType.Pause:
                return {
                    code: errorCodeTemplate.replace("{code}", ProgramRestriction.Pause.code),
                    message: ProgramRestriction.Pause.message
                };

            case MKProgramRestrictionType.Resume:
                return {
                    code: errorCodeTemplate.replace("{code}", ProgramRestriction.Resume.code),
                    message: ProgramRestriction.Resume.message
                };

            case MKProgramRestrictionType.SeekForward:
                return {
                    code: errorCodeTemplate.replace("{code}", ProgramRestriction.SeekForward.code),
                    message: ProgramRestriction.SeekForward.message
                };

            case MKProgramRestrictionType.SeekBackward:
                return {
                    code: errorCodeTemplate.replace("{code}", ProgramRestriction.SeekBackward.code),
                    message: ProgramRestriction.SeekBackward.message
                };

            case MKProgramRestrictionType.Restart:
                return {
                    code: errorCodeTemplate.replace("{code}", ProgramRestriction.Restart.code),
                    message: ProgramRestriction.Restart.message
                };

            case MKProgramRestrictionType.Timeshift:
                return {
                    code: errorCodeTemplate.replace("{code}", ProgramRestriction.Timeshift.code),
                    message: ProgramRestriction.Timeshift.message
                };
            
            case MKProgramRestrictionType.SeekSkip:
                return {
                    code: errorCodeTemplate.replace("{code}", ProgramRestriction.SeekSkip.code),
                    message: ProgramRestriction.SeekSkip.message
                };

            case MKProgramRestrictionType.CatchUp:
                return {
                    code: errorCodeTemplate.replace("{code}", ProgramRestriction.CatchUp.code),
                    message: ProgramRestriction.CatchUp.message
                };
            
            case MKProgramRestrictionType.AndroidBlocked:
                return {
                    code: errorCodeTemplate.replace("{code}", ProgramRestriction.AndroidBlocked.code),
                    message: ProgramRestriction.AndroidBlocked.message
                };

            case MKProgramRestrictionType.IOSBlocked:
                return {
                    code: errorCodeTemplate.replace("{code}", ProgramRestriction.IosBlocked.code),
                    message: ProgramRestriction.IosBlocked.message
                };

            case MKProgramRestrictionType.PhoneBlocked:
                return {
                    code: errorCodeTemplate.replace("{code}", ProgramRestriction.PhoneBlocked.code),
                    message: ProgramRestriction.PhoneBlocked.message
                };

            case MKProgramRestrictionType.TabletBlocked:
                return {
                    code: errorCodeTemplate.replace("{code}", ProgramRestriction.TabletBlocked.code),
                    message: ProgramRestriction.TabletBlocked.message
                };

            case MKProgramRestrictionType.DesktopBlocked:
                return {
                    code: errorCodeTemplate.replace("{code}", ProgramRestriction.DesktopBlocked.code),
                    message: ProgramRestriction.DesktopBlocked.message
                };

            case MKProgramRestrictionType.OutofhomeBlocked:
                return {
                    code: errorCodeTemplate.replace("{code}", ProgramRestriction.OutofhomeBlocked.code),
                    message: ProgramRestriction.OutofhomeBlocked.message
                };

            case MKProgramRestrictionType.AirplayBlocked:
                return {
                    code: errorCodeTemplate.replace("{code}", ProgramRestriction.AirplayBlocked.code),
                    message: ProgramRestriction.AirplayBlocked.message
                };

            //commenting the below two cases as it is currently not supported on this platform
            // case MKProgramRestrictionType.CellularBlocked:
            //     return {
            //         code: errorCodeTemplate.replace("{code}", ProgramRestriction.CellularBlocked.code),
            //         message: ProgramRestriction.CellularBlocked.message
            //     };

            // case MKProgramRestrictionType.WifiBlocked:
            //     return {
            //         code: errorCodeTemplate.replace("{code}", ProgramRestriction.WifiBlocked.code),
            //         message: ProgramRestriction.WifiBlocked.message
            //     };
            
        }
    }
}